import { runInAction } from "mobx"

import type { OrchestratedQuery } from "@/domains/library"
import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"

import type { IEpochGraphQlTransport, IEpochRepository } from "./interfaces"
import type { EpochApiData } from "./types"
import { EpochStore } from "./epoch-store"
import { convertEpochApiDataToDAO } from "./types"

export class EpochOrchestrator {
  public currentEpochQuery: OrchestratedQuery<EpochApiData | null> | undefined
  private historicEpochsQuery: OrchestratedQuery<EpochApiData[]> | undefined
  private minEpochNumber: number = 100

  constructor(
    private readonly epochRepository: IEpochRepository,
    private readonly epochGraphQlTransport: IEpochGraphQlTransport,
  ) {}

  initialize(
    currentEpochQuery: OrchestratedQuery<EpochApiData | null>,
    historicEpochsQuery: OrchestratedQuery<EpochApiData[]>,
  ): void {
    this.currentEpochQuery = currentEpochQuery
    this.historicEpochsQuery = historicEpochsQuery
  }

  /**
   * Load current epoch from GraphQL (triggered by OrchestratedQuery)
   */
  async onCurrentEpochQueryTrigger(
    _query: IOrchestratedQuerySender<EpochApiData | null>,
  ): Promise<EpochApiData | null> {
    return await this.epochGraphQlTransport.loadCurrentEpoch()
  }

  /**
   * Callback for when current epoch query succeeds
   */
  onCurrentEpochQuerySuccess(_sender: IOrchestratedQuerySender<EpochApiData | null>, data: EpochApiData | null): void {
    if (!data) {
      return
    }

    try {
      runInAction(() => {
        this.processCurrentEpoch(data)
      })
    } catch (error) {
      console.error("[EpochOrchestrator.onCurrentEpochQuerySuccess] Error processing current epoch data:", error)
    }
  }

  /**
   * Callback for when current epoch query fails
   */
  onCurrentEpochQueryFailed(_sender: IOrchestratedQuerySender<EpochApiData | null>, error: Error): void {
    console.error("[EpochOrchestrator.onCurrentEpochQueryFailed] Failed to load current epoch:", error)
  }

  /**
   * Load historic epochs from GraphQL (triggered by OrchestratedQuery)
   */
  async onHistoricEpochsQueryTrigger(_query: IOrchestratedQuerySender<EpochApiData[]>): Promise<EpochApiData[]> {
    return await this.epochGraphQlTransport.loadEpochs(this.minEpochNumber)
  }

  /**
   * Callback for when historic epochs query succeeds
   */
  onHistoricEpochsQuerySuccess(_sender: IOrchestratedQuerySender<EpochApiData[]>, data: EpochApiData[]): void {
    try {
      runInAction(() => {
        for (const epochData of data) {
          this.processEpoch(epochData)
        }
      })
    } catch (error) {
      console.error("[EpochOrchestrator.onHistoricEpochsQuerySuccess] Error processing historic epochs data:", error)
    }
  }

  /**
   * Callback for when historic epochs query fails
   */
  onHistoricEpochsQueryFailed(_sender: IOrchestratedQuerySender<EpochApiData[]>, error: Error): void {
    console.error("[EpochOrchestrator.onHistoricEpochsQueryFailed] Failed to load historic epochs:", error)
  }

  /**
   * Start the historic epochs query
   * Called by page store when analytics page mounts
   */
  startHistoricEpochsQuery(minEpochNumber: number): void {
    this.minEpochNumber = minEpochNumber
    if (this.historicEpochsQuery) {
      this.historicEpochsQuery.start()
    }
  }

  /**
   * Stop the historic epochs query
   * Called by page store when analytics page unmounts
   */
  stopHistoricEpochsQuery(): void {
    if (this.historicEpochsQuery) {
      this.historicEpochsQuery.stop()
    }
  }

  /**
   * Restart the historic epochs query with a new minEpochNumber
   * Updates the minEpochNumber and force executes the query immediately
   */
  restartHistoricEpochsQuery(minEpochNumber: number): void {
    this.minEpochNumber = minEpochNumber
    if (this.historicEpochsQuery) {
      this.historicEpochsQuery.forceExecute()
    }
  }

  /**
   * Process current epoch data and add/update in repository
   */
  private processCurrentEpoch(apiData: EpochApiData): void {
    const existing = this.epochRepository.find(apiData.epochNumber)

    if (existing) {
      // Update existing epoch
      if (existing instanceof EpochStore) {
        const dao = convertEpochApiDataToDAO(apiData)
        existing.updateData(dao)
      }
      return
    }

    // Create new epoch store
    const dao = convertEpochApiDataToDAO(apiData)
    const epochStore = new EpochStore(dao)
    this.epochRepository.add(epochStore)
  }

  /**
   * Process epoch data and add/update in repository
   */
  private processEpoch(apiData: EpochApiData): void {
    const existing = this.epochRepository.find(apiData.epochNumber)

    if (existing) {
      // Update existing epoch
      if (existing instanceof EpochStore) {
        const dao = convertEpochApiDataToDAO(apiData)
        existing.updateData(dao)
      }
      return
    }

    // Create new epoch store
    const dao = convertEpochApiDataToDAO(apiData)
    const epochStore = new EpochStore(dao)
    this.epochRepository.add(epochStore)
  }
}
